import { useEffect, useRef, useCallback } from "react";

/**
 * useAntigravityResize
 * Hook que utiliza ResizeObserver y requestAnimationFrame para actualizar
 * las dimensiones de Antigravity Canvas dinámicamente sin desmontar el componente.
 */
export function useAntigravityResize(onResizeCallback) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rafId = useRef(null);

  const handleResize = useCallback(() => {
    if (!containerRef.current || !canvasRef.current) return;

    const container = containerRef.current;
    const canvas = canvasRef.current;
    const { width, height } = container.getBoundingClientRect();

    if (width === 0 || height === 0) return;

    // Cap en dpr = 2 para rendimiento óptimo en pantallas retina móviles
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const displayWidth = Math.floor(width * dpr);
    const displayHeight = Math.floor(height * dpr);

    if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
      canvas.width = displayWidth;
      canvas.height = displayHeight;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      if (onResizeCallback) {
        onResizeCallback({
          width,
          height,
          displayWidth,
          displayHeight,
          dpr,
          isMobile: width < 768,
          isTablet: width >= 768 && width < 1024,
          isDesktop: width >= 1024,
        });
      }
    }
  }, [onResizeCallback]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(handleResize);
    });

    resizeObserver.observe(container);
    handleResize();

    window.addEventListener("orientationchange", handleResize);

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      resizeObserver.disconnect();
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [handleResize]);

  return { containerRef, canvasRef };
}
